import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as ReactRouterDom from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import RuleIcon from "@mui/icons-material/Rule";
import { applyAccessChanges, listAccessUsers } from "~/services/admin/access";
import { getApiErrorCode } from "~/utils/apiError";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { COLORS } from "~/ui/tokens";
import { EmptyState, LoadErrorBanner, PersonAvatar } from "../../shared/StatePanels";
import { ExternalChip, StatusChip } from "../chips";
import { accessErrorMessage, firstName, ROLE_OPTIONS } from "../labels";
import ChangesBar from "./ChangesBar";
import ConfirmChangesDialog from "./ConfirmChangesDialog";
import InactiveChip from "~/shared/ui/InactiveChip";
import {
  cellState, countChanges, describeChanges, dropUsers, effectiveRole, isRoleStaged, managersCount, setRole, summaryText, toChanges, toggleCompany,
} from "./stagedChanges";

// react-router 6.19+ exporta `useBlocker` (estável); versões anteriores só `unstable_useBlocker`.
const { UNSAFE_DataRouterContext } = ReactRouterDom;
const blockerExport = ["useBlocker", "unstable_useBlocker"].find((name) => typeof ReactRouterDom[name] === "function");
const useBlocker = ReactRouterDom[blockerExport];

const DEFAULT_FILTERS = { search: "", externalOnly: false, pendingOnly: false, hideDisabled: true };
// Teto do BE em todas as listas (spec §4.5, P1-5).
export const MATRIX_PAGE_SIZE = 100;
const roleValue = (role) => role ?? "";
const fromRoleValue = (value) => (value === "" ? null : value);
// Códigos que mantêm o diálogo aberto com a mensagem e recarregam a lista (P1-10). Textos: Apêndice.
const RELOAD_IN_DIALOG = {
  LAST_ADMIN: "Não é possível ficar sem nenhum Admin ativo. Recarregamos a lista; confira os Admins antes de salvar de novo.",
  USER_DISABLED: "Uma das pessoas foi desativada e não pode receber alterações. Recarregamos a lista.",
};
// Aviso de último Admin (spec §6.2); mesmo texto de LAST_ADMIN em labels.js. O BE continua barrando com 409.
const LAST_ADMIN_HINT = "Não é possível ficar sem nenhum Admin ativo.";
const FORBIDDEN_MESSAGE = "Seu acesso mudou enquanto você editava e não permite mais esta ação. Nada foi gravado; recarregamos suas permissões.";

// Navegação interna com alterações em estágio (final review I3). Texto sem mockup: Apêndice.
export const LEAVE_MATRIX_MESSAGE = "Há alterações não salvas na matriz. Sair sem salvar?";

// Só existe sob um data router (createBrowserRouter/RouterProvider) e só enquanto há alterações em estágio.
// Trocar de aba dentro de Acessos muda só o ?aba= (mesmo pathname) e não é bloqueado: a matriz continua montada.
function LeaveMatrixPrompt() {
  const shouldBlock = useCallback(({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname, []);
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(LEAVE_MATRIX_MESSAGE)) setTimeout(blocker.proceed, 0);
    else blocker.reset();
  }, [blocker]);
  return null;
}

export default function AccessMatrixTab({ reloadToken = 0, onLoaded, onOpenPerson, onForbidden }) {
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  // Trocar filtro volta à 1ª página; o estágio (por userId) não é tocado.
  const setFilters = (update) => {
    setFiltersState(update);
    setPage(0);
  };
  const [searchInput, setSearchInput] = useState("");
  const [data, setData] = useState({ status: "loading", users: [], companies: [], total: 0, activeAdminsCount: 0 });
  const [attempt, setAttempt] = useState(0);
  const [hiddenCompanyIds, setHiddenCompanyIds] = useState([]);
  const [staged, setStaged] = useState({});
  const [confirm, setConfirm] = useState({ open: false, saving: false, error: null });
  const notify = useNotify();
  const [staleMessage, setStaleMessage] = useState(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search === searchInput.trim()) return;
      setFiltersState((f) => ({ ...f, search: searchInput.trim() }));
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search]);

  useEffect(() => {
    const controller = new AbortController();
    setData((d) => ({ ...d, status: "loading" }));
    listAccessUsers({ ...filters, limit: MATRIX_PAGE_SIZE, offset: page * MATRIX_PAGE_SIZE }, { signal: controller.signal }).then(
      (result) => {
        if (controller.signal.aborted) return;
        setData({ status: "ok", ...result });
        onLoadedRef.current?.(result);
      },
      () => {
        if (!controller.signal.aborted) setData((d) => ({ ...d, status: "error" }));
      }
    );
    return () => controller.abort();
  }, [filters, page, reloadToken, attempt]);

  const changeCount = countChanges(staged);
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  useEffect(() => {
    if (!changeCount) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [changeCount]);

  const companiesById = useMemo(() => new Map(data.companies.map((c) => [c.id, c.name])), [data.companies]);
  const visibleCompanies = data.companies.filter((c) => !hiddenCompanyIds.includes(c.id));
  const items = useMemo(() => describeChanges(staged, companiesById), [staged, companiesById]);
  const reload = () => setAttempt((n) => n + 1);

  const save = async () => {
    setConfirm((c) => ({ ...c, saving: true, error: null }));
    try {
      await applyAccessChanges(toChanges(staged));
      setStaged({});
      setConfirm({ open: false, saving: false, error: null });
      notify({ severity: "success", message: "Alterações salvas." });
      setStaleMessage(null);
      reload();
    } catch (error) {
      if (getApiErrorCode(error) === "STALE_ACCESS") {
        const ids = error.response?.data?.userIds || [];
        const names = ids.map((id) => firstName(staged[id]?.user.name) || staged[id]?.user.email || `#${id}`).join(", ");
        setStaged((s) => dropUsers(s, ids));
        setConfirm({ open: false, saving: false, error: null });
        setStaleMessage(`Alguém alterou o acesso de ${names} antes de você. A matriz foi recarregada; refaça ${ids.length === 1 ? "essa alteração" : "essas alterações"}.`);
        reload();
        return;
      }
      const code = getApiErrorCode(error);
      if (code === "FORBIDDEN_ROLE") {
        // O ator perdeu o papel no meio do caminho: recarrega a lista e o auth/me (sem desmontar a página).
        // Outro 403 (ex.: ORIGIN_MISMATCH) cai no erro comum do diálogo, sem mexer no auth/me.
        setConfirm({ open: false, saving: false, error: null });
        setStaleMessage(FORBIDDEN_MESSAGE);
        reload();
        onForbidden?.();
        return;
      }
      if (RELOAD_IN_DIALOG[code]) {
        setConfirm((c) => ({ ...c, saving: false, error: RELOAD_IN_DIALOG[code] }));
        reload();
        return;
      }
      setConfirm((c) => ({ ...c, saving: false, error: accessErrorMessage(error, "Não foi possível salvar as alterações. Nada foi gravado.") }));
      // 404 / 409 (ex.: SELF_CHANGE_FORBIDDEN) / 422: mantém diálogo e estágio, mas recarrega a lista (contrato P1-10).
      if ([404, 409, 422].includes(error?.response?.status)) reload();
    }
  };

  const toolbar = (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, p: 2 }}>
      <TextField
        size="small"
        placeholder="Buscar pessoa"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        inputProps={{ "aria-label": "Buscar pessoa" }}
        sx={{ width: 250 }}
      />
      <FormControlLabel control={<Switch checked={filters.externalOnly} onChange={(e) => setFilters((f) => ({ ...f, externalOnly: e.target.checked }))} />} label="Mostrar só externos" />
      <FormControlLabel control={<Switch checked={filters.pendingOnly} onChange={(e) => setFilters((f) => ({ ...f, pendingOnly: e.target.checked }))} />} label="Só pendentes" />
      <FormControlLabel control={<Switch checked={filters.hideDisabled} onChange={(e) => setFilters((f) => ({ ...f, hideDisabled: e.target.checked }))} />} label="Ocultar desativados" />
      <Box sx={{ flexGrow: 1 }} />
      <TextField
        select
        size="small"
        label="Colunas de empresa"
        value={visibleCompanies.map((c) => c.id)}
        onChange={(e) => {
          const shown = e.target.value;
          setHiddenCompanyIds(data.companies.map((c) => c.id).filter((id) => !shown.includes(id)));
        }}
        SelectProps={{
          multiple: true,
          renderValue: (selected) => (selected.length === data.companies.length ? `Todas (${data.companies.length})` : `${selected.length} de ${data.companies.length}`),
        }}
        sx={{ minWidth: 190 }}
      >
        {data.companies.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            <Checkbox size="small" checked={!hiddenCompanyIds.includes(c.id)} />
            <ListItemText primary={c.name} />
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const header = (
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600, minWidth: 260 }}>Pessoa</TableCell>
        <TableCell sx={{ fontWeight: 600, minWidth: 140, bgcolor: "#f0fafd" }}>Papel Antecipe</TableCell>
        {visibleCompanies.map((c) => (
          <TableCell key={c.id} align="center" sx={{ fontWeight: 600, minWidth: 96, color: c.active === false ? COLORS.muted : undefined }}>
            {c.name}
            {c.active === false && <InactiveChip sx={{ ml: 0.5 }} />}
            <Typography component="div" variant="caption" sx={{ color: COLORS.muted, fontWeight: 400 }}>Gestor</Typography>
          </TableCell>
        ))}
        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
      </TableRow>
    </TableHead>
  );

  const roleCell = (user) => {
    const disabled = user.isSelf || user.status === "disabled";
    const lastAdmin = user.role === "admin" && user.status === "active" && data.activeAdminsCount === 1;
    const select = (
      <TextField
        select
        size="small"
        value={roleValue(effectiveRole(staged, user))}
        onChange={(e) => setStaged((s) => setRole(s, user, fromRoleValue(e.target.value)))}
        disabled={disabled}
        SelectProps={{ native: true, IconComponent: user.isSelf ? LockOutlinedIcon : undefined }}
        inputProps={{ "aria-label": `Papel de ${user.name || user.email}` }}
        sx={{ width: 120, "& .MuiOutlinedInput-root": isRoleStaged(staged, user.id) ? { outline: `2px solid ${COLORS.accent}` } : {} }}
      >
        {ROLE_OPTIONS.map((o) => (
          <option key={o.label} value={roleValue(o.value)} disabled={lastAdmin && o.value !== "admin"}>
            {o.label}
          </option>
        ))}
      </TextField>
    );
    if (lastAdmin && !user.isSelf) {
      return (
        <Tooltip title={LAST_ADMIN_HINT} placement="bottom-start">
          <span data-testid={`last-admin-role-${user.id}`}>{select}</span>
        </Tooltip>
      );
    }
    if (!user.isSelf) return select;
    return (
      <Tooltip title="Você não pode alterar o próprio papel" placement="bottom-start">
        <span data-testid={`self-role-${user.id}`}>{select}</span>
      </Tooltip>
    );
  };

  const companyCell = (user, company) => {
    const state = cellState(staged, user, company.id);
    // Empresa inativa não aceita vínculo novo (422 COMPANY_INACTIVE); remover e manter o existente continuam permitidos (Q7).
    const blocked = company.active === false && state === "off";
    const label = `${user.name || user.email} gestor de ${company.name}${blocked ? " (empresa inativa: não aceita vínculo novo)" : ""}`;
    return (
      <TableCell key={company.id} align="center" sx={{ bgcolor: state === "add" || state === "remove" ? "#e0f5fb" : undefined, borderLeft: state === "add" || state === "remove" ? `3px solid ${COLORS.accent}` : undefined }}>
        <Checkbox
          checked={state === "on" || state === "add"}
          disabled={user.status === "disabled" || blocked}
          onChange={() => setStaged((s) => toggleCompany(s, user, company.id))}
          inputProps={{ "aria-label": label }}
          sx={{
            color: state === "remove" ? "#d32f2f" : undefined,
            "&.Mui-checked": { color: state === "add" ? COLORS.accent : COLORS.primary },
          }}
        />
      </TableCell>
    );
  };

  let body;
  if (data.status === "loading" && data.users.length === 0) {
    body = Array.from({ length: 8 }).map((_, i) => (
      <TableRow key={i} data-testid="matrix-skeleton-row">
        <TableCell><Box sx={{ display: "flex", gap: 1, alignItems: "center" }}><Skeleton variant="circular" width={28} height={28} /><Skeleton width={180} /></Box></TableCell>
        <TableCell><Skeleton width={100} height={32} /></TableCell>
        {visibleCompanies.map((c) => <TableCell key={c.id} align="center"><Skeleton width={18} height={18} sx={{ mx: "auto" }} /></TableCell>)}
        <TableCell><Skeleton width={70} /></TableCell>
      </TableRow>
    ));
  } else {
    body = data.users.map((user) => (
      <TableRow key={user.id} hover>
        <TableCell>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <PersonAvatar name={user.name} email={user.email} size={28} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                <ButtonBase onClick={() => onOpenPerson?.(user.id)} sx={{ fontWeight: 600, color: COLORS.text, fontSize: 14, fontFamily: "inherit" }}>
                  {user.name || user.email}
                </ButtonBase>
                {user.isSelf && <Typography variant="caption" sx={{ color: COLORS.muted }}>(você)</Typography>}
                {user.isExternal && <ExternalChip />}
              </Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 230 }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ bgcolor: "#f0fafd" }}>{roleCell(user)}</TableCell>
        {visibleCompanies.map((company) => companyCell(user, company))}
        <TableCell><StatusChip user={user} /></TableCell>
      </TableRow>
    ));
  }

  return (
    <Box>
      {dataRouter && changeCount > 0 && <LeaveMatrixPrompt />}
      {toolbar}
      {staleMessage && <Alert severity="warning" sx={{ mx: 2, mb: 1 }} onClose={() => setStaleMessage(null)}>{staleMessage}</Alert>}
      {data.status === "error" ? (
        <Box sx={{ px: 2 }}>
          <LoadErrorBanner message="Não foi possível carregar os acessos. Tente novamente em instantes." onRetry={reload} />
          <EmptyState icon={CloudOffOutlinedIcon} title="Matriz indisponível" text="Verifique sua conexão. Se persistir, avise o time de tecnologia." />
        </Box>
      ) : data.status === "ok" && data.users.length === 0 ? (
        <EmptyState icon={PersonSearchOutlinedIcon} title="Ninguém encontrado" text={filters.search ? `Nenhuma pessoa com "${filters.search}".` : "Nenhuma pessoa com esses filtros."} />
      ) : (
        <>
          <TableContainer sx={{ maxHeight: "calc(100vh - 380px)", minHeight: 240 }}>
            <Table stickyHeader size="small" aria-label="Matriz de acessos">
              {header}
              <TableBody>
                {body}
                {data.status === "ok" && (
                  <TableRow data-testid="managers-row" sx={{ "& td": { bgcolor: "#f9fafb", color: COLORS.muted } }}>
                    <TableCell colSpan={2}>Gestores por empresa</TableCell>
                    {visibleCompanies.map((c) => {
                      const n = managersCount(c, staged);
                      return (
                        <TableCell key={c.id} align="center">
                          {n}
                          {n === 0 && <WarningAmberIcon aria-label="Empresa sem gestor" sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />}
                        </TableCell>
                      );
                    })}
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {data.status === "ok" && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", px: 2, py: 1.5, borderTop: "1px solid #e5e7eb", fontSize: 13, color: COLORS.muted }}>
              <span><Checkbox size="small" checked disabled sx={{ p: 0, mr: 0.5 }} inputProps={{ "aria-hidden": true }} />gestor</span>
              <span><Checkbox size="small" checked disabled sx={{ p: 0, mr: 0.5, "&.Mui-disabled": { color: COLORS.accent } }} inputProps={{ "aria-hidden": true }} />a adicionar</span>
              <span><Checkbox size="small" disabled sx={{ p: 0, mr: 0.5, "&.Mui-disabled": { color: "#d32f2f" } }} inputProps={{ "aria-hidden": true }} />a remover</span>
              <Box sx={{ flexGrow: 1 }} />
              <RuleIcon sx={{ fontSize: 15 }} aria-hidden />
              <span>{`Ninguém altera o próprio papel · o último Admin ativo não pode ser removido · ${data.users.length} de ${data.total} pessoas`}</span>
            </Box>
          )}
          {data.total > MATRIX_PAGE_SIZE && (
            <TablePagination
              component="div"
              count={data.total}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={MATRIX_PAGE_SIZE}
              rowsPerPageOptions={[MATRIX_PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              getItemAriaLabel={(type) => (type === "next" ? "Próxima página" : type === "previous" ? "Página anterior" : type)}
            />
          )}
        </>
      )}
      <ChangesBar count={changeCount} summary={summaryText(items)} onDiscard={() => setStaged({})} onSave={() => setConfirm({ open: true, saving: false, error: null })} />
      <ConfirmChangesDialog
        open={confirm.open}
        count={changeCount}
        items={items}
        saving={confirm.saving}
        error={confirm.error}
        onBack={() => setConfirm({ open: false, saving: false, error: null })}
        onConfirm={save}
      />
    </Box>
  );
}
